import React from 'react'
import _ from 'lodash'
import moment from 'moment-timezone'
import 'moment/locale/en-gb'

import TicketInvoicingTimesheet from './Timesheets'
import {
  findChargeableDuration,
  minutesToHours,
  timesheetDuration
} from './TimesheetUtilities'

import Alert from 'Components/Alert'
import ErrorList from 'Components/ErrorList'
import { cloudhelpPatch, cloudhelpGet } from 'Utilities/CloudHelp'

export default class TicketInvoicing extends React.Component {
  static sumActualTimesheetHours (timesheets) {
    const timesheetHours = _.sumBy(timesheets, (timesheet) => {
      const endTime = moment(timesheet.end_time)
      const startTime = moment(timesheet.start_time)

      return (endTime.seconds(0).diff(startTime.seconds(0), 'minutes') / 60.0)
    })

    const { hours, minutes } = minutesToHours(_.round(timesheetHours, 2) * 60)

    return `${hours} hours, ${minutes} minutes`
  }

  static sumChargeableTimesheetHours (timesheets) {
    const timesheetHours = _.sumBy(timesheets, 'timesheet_duration')

    const { hours, minutes } = minutesToHours(_.round(timesheetHours, 2) * 60)

    return `${hours} hours, ${minutes} minutes`
  }

  static totalToBeBilled (timesheets) {
    let total = 0

    _.each(timesheets, (timesheet) => {
      total += _.round(timesheet.chargeable_rate * timesheet.timesheet_duration, 2)
    })

    return total
  }

  static toErrorMessage (field, error) {
    let fieldName = _.capitalize(field)
    fieldName = _.replace(fieldName, /_id/g, '')
    fieldName = _.replace(fieldName, /_/g, ' ')

    return `${fieldName} ${error}`
  }

  constructor (props) {
    super(props)

    const { ticket, timesheets } = props

    this.state = {
      flashType: 'error',
      chargeable_rate: ticket.chargeable_rate || '75',
      showAlert: false,
      flashText: 'There was an issue with this ticket.',
      errors: [],
      serviceGroups: [],
      workTypes: [],
      workRoles: [],
      submitButtonText: 'Update',
      submitButtonClasses: 'button',
      timesheets
    }
  }

  componentWillMount () {
    this.getServiceGroups()
    this.getWorkItems('workTypes')
    this.getWorkItems('workRoles')
  }

  getWorkItems = (type) => {
    const { paths, ticket: { region_id } } = this.props
    const { path } = _.defaultTo(_.find(paths, ['name', type]), {})

    cloudhelpGet(path, { region_id })
      .then((response) => { this.setState({ [type]: response.data }) })
      .catch((_error) => {
        this.setState({
          showAlert: true,
          flashType: 'error',
          flashText: 'There was an issue with retrieving work items'
        })
      })
  }

  getServiceGroups = () => {
    const { paths } = this.props
    const { path } = _.defaultTo(_.find(paths, ['name', 'clientServiceGroups']), {})

    cloudhelpGet(path)
      .then((response) => {
        this.setState({ serviceGroups: response.data })
      })
      .catch((_error) => {
        this.setState({
          showAlert: true,
          flashType: 'error',
          flashText: 'There was an issue with retrieving work items'
        })
      })
  }

  componentWillReceiveProps = (newProps) => {
    const { timesheets } = this.props

    if (newProps.timesheets !== timesheets) {
      const newTimesheets = _.map(newProps.timesheets, timesheet => (
        Object.assign({}, timesheet, { description: `Time for ${timesheet.time_spent} by ${timesheet.user_name}\n${timesheet.description}` })
      ))

      this.setState({ timesheets: newTimesheets }, () => {
        const { paths, ticket } = this.props
        const invoicingPath = paths.find(path => (path.name === 'invoiceTimesheets')).path

        if (_.isNull(ticket.reviewed_by) || _.isNull(ticket.reviewed_at)) {
          return Promise.resolve()
        }

        cloudhelpGet(invoicingPath)
          .then((response) => {
            this.setState((prevState) => {
              const { timesheets: previousTimesheets } = prevState

              const timesheetsToKeep = _.filter(previousTimesheets, (timesheet) => {
                const withinInvoiceTimesheets = _.includes(
                  _.map(response.data.timesheets, 'original_timesheet_id'),
                  timesheet.id
                )

                return !withinInvoiceTimesheets
              })
              const newTimesheetsToKeep = [...response.data.timesheets, ...timesheetsToKeep]

              return { timesheets: newTimesheetsToKeep }
            })
          })
      })
    }
  }

  setFieldErrorClasses (field) {
    const { errors } = this.state
    if (_.includes(errors, field)) {
      return ('error')
    }

    return null
  }

  changeTimesheetField = (field, value, index) => {
    this.setState((prevState) => {
      const { timesheets, workTypes } = prevState
      const timesheet = timesheets[index]
      timesheet[field] = value

      let billingDetails = _.get(_.find(workTypes, ['id', timesheet.work_type_id]), 'billing_details')
      if (field === 'work_type_id') {
        billingDetails = _.get(_.find(workTypes, ['id', value]), 'billing_details')
        timesheet.billing_details = billingDetails
      }

      if (_.includes(['work_type_id', 'start_time', 'end_time'], field)) {
        const duration = timesheetDuration(timesheet)

        timesheet.timesheet_duration = findChargeableDuration(duration, billingDetails)
      }

      return { timesheets }
    })
  }

  handleInputChange (event, fieldName) {
    const { target } = event
    const value = target.type === 'checkbox' ? target.checked : target.value

    this.setState({ [fieldName]: value })
  }

  submitForm (event, submitCallback) {
    event.preventDefault()

    const { chargeable_rate, timesheets } = this.state
    const { paths, ticket } = this.props
    const invoicingPath = paths.find(path => (path.name === 'invoiceTimesheets')).path

    this.setState({
      submitButtonText: 'Updating...',
      submitButtonClasses: 'button button--disabled'
    })

    const patchData = {
      ticket: { chargeable_rate },
      timesheets: _.map(timesheets, timesheet => (
        {
          id: timesheet.id,
          description: timesheet.description,
          start_time: timesheet.start_time,
          end_time: timesheet.end_time,
          work_type_id: timesheet.work_type_id,
          chargeable_rate: timesheet.chargeable_rate,
          chargeable: timesheet.chargeable
        }
      ))
    }

    cloudhelpPatch(invoicingPath, patchData)
      .then((_response) => {
        this.setState({
          showAlert: true,
          flashType: 'success',
          flashText: 'Ticket invoicing updated.',
          submitButtonText: 'Update',
          submitButtonClasses: 'button'
        }, () => submitCallback(_.map(timesheets, t => ({ ...t, ticket }))))
      })
      .catch((error) => {
        const responseErrors = _.get(error, 'response.data.errors[0]', {})
        const flashText = (errorResponse) => {
          if (errorResponse.response.status === 500) {
            return 'There was an issue updating your ticket.'
          }

          return _(responseErrors)
            .map((msg, field) => this.constructor.toErrorMessage(field, msg))
            .uniq()
            .value()
        }

        this.setState({
          showAlert: true,
          errors: responseErrors,
          flashType: 'error',
          flashText: flashText(error),
          submitButtonText: 'Update',
          submitButtonClasses: 'button'
        })
      })
  }

  copyField (event, newState) {
    event.preventDefault()
    this.setState(newState)
  }

  render () {
    const { changeTimesheetField } = this
    const {
      flashType,
      showAlert,
      flashText,
      chargeable_rate,
      submitButtonText,
      submitButtonClasses,
      timesheets,
      workTypes,
      workRoles,
      serviceGroups
    } = this.state

    const {
      hidden,
      ticket,
      paths,
      timesheets: originalTimesheets,
      card,
      submitCallback
    } = this.props

    const { sumActualTimesheetHours, sumChargeableTimesheetHours } = this.constructor

    const timesheetHours = sumActualTimesheetHours(originalTimesheets)
    const chargeableTimesheetHours = sumChargeableTimesheetHours(_.filter(timesheets, ['chargeable', true]))
    const invoiceTimesheetHours = sumActualTimesheetHours(
      _(timesheets).difference(originalTimesheets).isEmpty() ? [] : timesheets
    )

    const timesheetList = _.map(timesheets, (timesheet, index) => (
      <TicketInvoicingTimesheet
        key={index}
        index={index}
        workTypes={workTypes}
        workRoles={workRoles}
        serviceGroups={serviceGroups}
        timesheet={timesheet}
        changeTimesheetField={changeTimesheetField}
      />
    ))

    return (
      <section className={card ? 'card card--allow-overflow' : null} style={hidden ? { display: 'none' } : {}}>
        <form>
          <div className="content-section-block">

            <h3 className="content-section-block__title">
              <span className="content-section-block__title-header">
                Invoice information (Ticket <a href={ticket.href} target="blank">#{ticket.id}</a>)
              </span>
              <div className="content-section-block__title-actions">
                {ticket.invoiceId && (
                  <a style={{ marginRight: '20px' }} target="_blank" rel="noopener noreferrer" href={_.find(paths, ['name', 'clientInvoice']).path}>
                    Show invoice
                  </a>
                )}
              </div>
            </h3>

            {flashType === 'error' && (
              <Alert alertType={flashType} showAlert={showAlert}>
                <ErrorList errors={flashText} />
              </Alert>
            )}

            {flashType === 'success' && (
              <Alert alertType={flashType} showAlert={showAlert} text={flashText} />
            )}

            <div className="info-box info-box--bottom-spaced">
              <div className="info-box__item">
                <span className="lnr lnr-timer2" />
                <p>
                  {timesheetHours}
                </p>
                <p className="info-box__item-description">
                  Total actual time logged by technicians
                </p>
              </div>

              <div className="info-box__item">
                <span className="lnr lnr-timer2" />
                <p>
                  {invoiceTimesheetHours}
                </p>
                <p className="info-box__item-description">
                  Total logged for billing
                </p>
              </div>

              <div className="info-box__item">
                <span className="lnr lnr-receipt" />
                <p>
                  {chargeableTimesheetHours}
                </p>
                <p className="info-box__item-description">
                  Total billable time (rounded)
                </p>
              </div>
              <div className="info-box__item">
                <span className="lnr lnr-receipt" />
                <p>
                  {_.round(
                    this.constructor.totalToBeBilled(_.filter(timesheets, ['chargeable', true])),
                    2
                  )}
                </p>
                <p className="info-box__item-description">
                  Total billable amount (in clients currency)
                </p>
              </div>
            </div>

            <div className="row">
              { ticket.reviewed_at && ticket.reviewed_by && (
                <div className="col-12">
                  Invoicing reviewed by
                  {' '}
                  {ticket.reviewed_by}
                  {' '}
                  on
                  {' '}
                  {moment(ticket.reviewed_at).format('DD MMMM YYYY, hh:mmA')}
                </div>
              ) }
            </div>
            <hr />

            {timesheetList}

            <div className="form-section">
              <div className="form-section__actions form-section__actions--space-between form-section__actions--no-margin">
                <button type="submit" className={submitButtonClasses} onClick={(event) => this.submitForm(event, submitCallback)}>
                  {submitButtonText}
                </button>
              </div>
            </div>
          </div>
        </form>
      </section>
    )
  }
}

TicketInvoicing.defaultProps = {
  card: true,
  submitCallback: () => undefined
}
